import React, { ReactElement, useState } from 'react';
import { NavigateFunction, Params, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import {
  AccountabilityTemplate,
  Amount,
  CharityActivity,
  Collection,
  Condition,
  Measurement,
  MeasurementDiagram,
  MemberLeaderboard as MemberLeaderboardType,
  OptionalExercise,
  Programme,
  RecommendedExercise,
  Resource,
  SelfCareActivity,
  Session,
  SessionTemplate,
  TeamLeaderboard as TeamLeaderboardType,
  Weighting
} from '../../../../types/entity.types';

import { ACTIVE, ARCHIVED, UPCOMING, MEMBERS, TEAMS } from '../../../../utils/constants';
import { getClassNames } from '../../../../utils/util';
import { checkForAccountabilityChanges } from '../../../../utils/diff-util';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import AccountabilityWeightings from './accountability-weightings/AccountabilityWeightings';
import AccountabilityMeasurements from './accountability-measurements/AccountabilityMeasurements';
import AccountabilityNutrition from './accountability-nutrition/AccountabilityNutrition';
import AccountabilityCondition from './accountability-condition/AccountabilityCondition';
import AccountabilityMovement from './accountability-movement/AccountabilityMovement';
import AccountabilityCharity from './accountability-charity/AccountabilityCharity';
import AccountabilitySelfCare from './accountability-self-care/AccountabilitySelfCare';
import AccountabilityAttendance from './accountability-attendance/AccountabilityAttendance';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import MemberLeaderboard from './leaderbaord/MemberLeaderboard';
import TeamLeaderboard from './leaderbaord/TeamLeaderboard';

import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';

import './accountability.scss';

type Props = {
  status: string;
  programme: Programme;
  leaderboard: MemberLeaderboardType;
  teamLeaderboard?: TeamLeaderboardType;
  accountabilityTemplate?: AccountabilityTemplate;
  collections?: Collection[];
  weightings?: Weighting;
  measurement?: Measurement;
  mealPlans?: Resource[];
  conditions?: Condition[];
  recommendedExercises?: RecommendedExercise[];
  optionalExercises?: OptionalExercise[];
  charityActivities?: CharityActivity[];
  selfCareActivities?: SelfCareActivity[];
  sessions?: Session[];
  updateWeighting: (key: keyof Weighting, value: number) => void;
  updateMeasurement: (key: keyof Measurement, value: MeasurementDiagram | string | undefined) => void;
  createMealPlan: (collectionExternalReference: string, mealPlan: Resource) => void;
  updateMealPlan: (resource: Resource) => void;
  deleteMealPlan: (collectionExternalReference: string, uuid: string) => void;
  createCondition: () => void;
  updateCondition: (externalReference: string, key: keyof Condition, value: string) => void;
  deleteCondition: (externalReference: string) => void;
  createRecommendedExercise: () => void;
  updateRecommendedExercise: (
    externalReference: string,
    key: keyof Omit<RecommendedExercise, 'amounts'>,
    value: string
  ) => void;
  deleteRecommendedExercise: (externalReference: string) => void;
  createExerciseAmount: (exerciseExternalReference: string) => void;
  updateExerciseAmount: (
    exerciseExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteExerciseAmount: (exerciseExternalReference: string, amountExternalReference: string) => void;
  createOptionalExercise: () => void;
  updateOptionalExercise: (externalReference: string, key: keyof OptionalExercise, value: string) => void;
  deleteOptionalExercise: (externalReference: string) => void;
  createCharityActivity: () => void;
  updateCharityActivity: (
    externalReference: string,
    key: keyof Omit<CharityActivity, 'amounts'>,
    value: string
  ) => void;
  deleteCharityActivity: (externalReference: string) => void;
  createCharityAmount: (activityExternalReference: string) => void;
  updateCharityAmount: (
    activityExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteCharityAmount: (activityExternalReference: string, amountExternalReference: string) => void;
  createSelfCareActivity: () => void;
  updateSelfCareActivity: (externalReference: string, value: string) => void;
  deleteSelfCareActivity: (externalReference: string) => void;
  updateSession: (session: Partial<Session>) => void;
  deleteSession: (externalReference: string) => void;
  saveAccountabilityTemplate: (redirectUrl?: string) => void;
  generateSessions: (sessionTemplate: SessionTemplate, onSuccess: () => void) => void;
  downloadLeaderboardExcel: () => void;
  downloadDocument: (resource: Resource) => void;
  downloadTeamLeaderboardExcel: () => void;
};

const Accountability = ({
  status,
  programme,
  leaderboard,
  teamLeaderboard,
  accountabilityTemplate,
  collections = [],
  weightings,
  measurement,
  mealPlans = [],
  conditions = [],
  recommendedExercises = [],
  optionalExercises = [],
  charityActivities = [],
  selfCareActivities = [],
  sessions = [],
  updateWeighting,
  updateMeasurement,
  createMealPlan,
  updateMealPlan,
  deleteMealPlan,
  createCondition,
  updateCondition,
  deleteCondition,
  createRecommendedExercise,
  updateRecommendedExercise,
  deleteRecommendedExercise,
  createExerciseAmount,
  updateExerciseAmount,
  deleteExerciseAmount,
  createOptionalExercise,
  updateOptionalExercise,
  deleteOptionalExercise,
  createCharityActivity,
  updateCharityActivity,
  deleteCharityActivity,
  createCharityAmount,
  updateCharityAmount,
  deleteCharityAmount,
  createSelfCareActivity,
  updateSelfCareActivity,
  deleteSelfCareActivity,
  updateSession,
  deleteSession,
  saveAccountabilityTemplate,
  generateSessions,
  downloadLeaderboardExcel,
  downloadDocument,
  downloadTeamLeaderboardExcel
}: Props): ReactElement => {
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);

  const navigate: NavigateFunction = useNavigate();

  const { programmeExternalReference, view }: Params = useParams();

  const checkForUnsavedChanges = (url: string): void => {
    if (
      checkForAccountabilityChanges(
        accountabilityTemplate,
        weightings,
        measurement,
        conditions,
        recommendedExercises,
        optionalExercises,
        selfCareActivities,
        sessions,
        charityActivities
      )
    ) {
      setRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const handleToggleClick = (newView: string): void => {
    if (newView === view) return;

    navigate(`/programmes/${status}/${programmeExternalReference}/accountability/${newView}`);
  };

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="accountability__icons">
        {((settingsOpen && status !== ARCHIVED) || status === UPCOMING) && (
          <div className="accountability__icon-container" onClick={(): void => saveAccountabilityTemplate()}>
            <SaveIcon className="accountability__save-icon" data-testid="save-icon" />
          </div>
        )}
        {!settingsOpen && status !== UPCOMING && (
          <div className="accountability__icon-container" onClick={(): void => setSettingsOpen(true)}>
            <GearIcon className="accountability__gear-icon" data-testid="cog-icon" />
          </div>
        )}
        {settingsOpen && status !== UPCOMING && (
          <div className="accountability__icon-container" onClick={(): void => setSettingsOpen(false)}>
            <CloseIcon className="accountability__close-icon" data-testid="times-icon" />
          </div>
        )}
      </div>

      {!settingsOpen && (status === ACTIVE || status === ARCHIVED) && (
        <div className="accountability" data-testid={`accountability-${status}`}>
          <div className="accountability__leaderboard-header">
            <h1 className="">Leaderboard</h1>
            <div className="accountability__toggle">
              <button
                className={getClassNames('accountability__toggle-button', { active: view === MEMBERS })}
                onClick={(): void => handleToggleClick(MEMBERS)}
              >
                Members
              </button>
              <button
                className={getClassNames('accountability__toggle-button', { active: view === TEAMS })}
                onClick={(): void => handleToggleClick(TEAMS)}
              >
                Teams
              </button>
            </div>
          </div>

          {view === TEAMS ? (
            <TeamLeaderboard leaderboard={teamLeaderboard} downloadExcel={downloadTeamLeaderboardExcel} />
          ) : (
            <MemberLeaderboard leaderboard={leaderboard} downloadExcel={downloadLeaderboardExcel} />
          )}
          <div className="accountability__footer" />
        </div>
      )}

      {(settingsOpen || status === UPCOMING) && (
        <div className="accountability" data-testid={`accountability-${status}`}>
          <div
            className={getClassNames('accountability__settings-header', {
              short: status !== ACTIVE
            })}
          >
            {status === ACTIVE && (
              <p className="accountability__warning">
                PLEASE NOTE: Editing an active programme should be limited to correcting spelling or numeric mistakes
              </p>
            )}
          </div>
          <div
            className={getClassNames('accountability__settings-list', {
              long: status !== ACTIVE
            })}
          >
            <AccountabilityWeightings
              programme={programme}
              status={status}
              weightings={weightings}
              updateWeighting={updateWeighting}
            />
            <hr className="accountability__divider" />
            <AccountabilityMeasurements
              status={status}
              measurement={measurement}
              updateMeasurement={updateMeasurement}
            />
            <hr className="accountability__divider" />
            <AccountabilityNutrition
              status={status}
              collections={collections}
              mealPlans={mealPlans}
              createMealPlan={createMealPlan}
              updateMealPlan={updateMealPlan}
              deleteMealPlan={deleteMealPlan}
              downloadDocument={downloadDocument}
            />
            <hr className="accountability__divider" />
            <AccountabilityCondition
              status={status}
              conditions={conditions}
              createCondition={createCondition}
              updateCondition={updateCondition}
              deleteCondition={deleteCondition}
            />
            <hr className="accountability__divider" />
            <AccountabilityMovement
              status={status}
              programme={programme}
              recommendedExercises={recommendedExercises}
              optionalExercises={optionalExercises}
              createRecommendedExercise={createRecommendedExercise}
              updateRecommendedExercise={updateRecommendedExercise}
              deleteRecommendedExercise={deleteRecommendedExercise}
              createExerciseAmount={createExerciseAmount}
              updateExerciseAmount={updateExerciseAmount}
              deleteExerciseAmount={deleteExerciseAmount}
              createOptionalExercise={createOptionalExercise}
              updateOptionalExercise={updateOptionalExercise}
              deleteOptionalExercise={deleteOptionalExercise}
            />
            {programme?.charityWork && (
              <>
                <hr className="accountability__divider" />
                <AccountabilityCharity
                  status={status}
                  programme={programme}
                  charityActivities={charityActivities}
                  createCharityActivity={createCharityActivity}
                  updateCharityActivity={updateCharityActivity}
                  deleteCharityActivity={deleteCharityActivity}
                  createCharityAmount={createCharityAmount}
                  updateCharityAmount={updateCharityAmount}
                  deleteCharityAmount={deleteCharityAmount}
                />
              </>
            )}
            <hr className="accountability__divider" />
            <AccountabilitySelfCare
              status={status}
              selfCareActivities={selfCareActivities}
              createSelfCareActivity={createSelfCareActivity}
              updateSelfCareActivity={updateSelfCareActivity}
              deleteSelfCareActivity={deleteSelfCareActivity}
            />
            <hr className="accountability__divider" />
            <AccountabilityAttendance
              status={status}
              sessions={sessions}
              updateSession={updateSession}
              deleteSession={deleteSession}
              generateSessions={generateSessions}
            />
          </div>
        </div>
      )}

      <SaveChangesModal
        redirectUrl={redirectUrl}
        handleSaveClick={(): void => saveAccountabilityTemplate(redirectUrl)}
      />
    </ProgrammeLayout>
  );
};

export default Accountability;
